'use client';

import { useCallback, useEffect, useRef, type MouseEvent } from 'react';

/**
 * Lazy-trailing cursor hook shared by CaseStudy and GitHubRepoWidget.
 *
 * Writes `--hx`, `--hy`, and `--holo-opacity` CSS custom properties on the
 * bound element so the hover highlight can be driven in CSS. The lerp is
 * intentionally slow (0.035) and layered with a gentle sine drift so the
 * highlight feels alive instead of snapping to the cursor.
 */
export function useHoloCursor() {
  const ref = useRef<HTMLDivElement>(null);
  const state = useRef({
    tx: 0.5,
    ty: 0.5, // target (actual cursor)
    cx: 0.5,
    cy: 0.5, // current (lazy-lerped position)
    hovering: false,
    raf: 0,
  });

  const startLoop = useCallback(() => {
    const s = state.current;
    const el = ref.current;
    if (!el) return;

    const tick = () => {
      if (!s.hovering) return;

      s.cx += (s.tx - s.cx) * 0.035;
      s.cy += (s.ty - s.cy) * 0.035;

      const t = performance.now() * 0.001;
      const driftX = Math.sin(t * 0.8) * 0.06;
      const driftY = Math.cos(t * 1.1) * 0.06;

      const hx = Math.max(0, Math.min(1, s.cx + driftX));
      const hy = Math.max(0, Math.min(1, s.cy + driftY));

      el.style.setProperty('--hx', hx.toFixed(3));
      el.style.setProperty('--hy', hy.toFixed(3));

      s.raf = requestAnimationFrame(tick);
    };

    s.raf = requestAnimationFrame(tick);
  }, []);

  const onMouseMove = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const s = state.current;
      s.tx = (e.clientX - rect.left) / rect.width;
      s.ty = (e.clientY - rect.top) / rect.height;

      if (!s.hovering) {
        s.hovering = true;
        s.cx = s.tx + (Math.random() > 0.5 ? 0.25 : -0.25);
        s.cy = s.ty + (Math.random() > 0.5 ? 0.25 : -0.25);
        el.style.setProperty('--holo-opacity', '1');
        startLoop();
      }
    },
    [startLoop],
  );

  const onMouseLeave = useCallback(() => {
    const s = state.current;
    const el = ref.current;
    s.hovering = false;
    cancelAnimationFrame(s.raf);
    if (el) el.style.setProperty('--holo-opacity', '0');
  }, []);

  useEffect(() => {
    return () => cancelAnimationFrame(state.current.raf);
  }, []);

  return { ref, onMouseMove, onMouseLeave };
}
